import React, { useState } from "react";
import {
  Box, Tab, Tabs, Typography, TextField, IconButton,
  Chip, Paper, Grid, Button, Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  updateContactoAction,
  updateEcografiasAction,
  updateEstudiosAction,
  updateConsultasAction,
  updateModeloEcoAction,
  updateConveniosAction,
  updateEspecialidadesAction,
  updateDisplayTimezoneAction,
} from "../redux/actions/settings";

const TabPanel = ({ value, index, children }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const SectionLabel = ({ children }) => (
  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2, mb: 1 }}>
    {children}
  </Typography>
);

const Settings = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings);

  const [tab, setTab] = useState(0);

  // Contacto
  const [contacto, setContacto] = useState(settings.contacto);

  // Especialidades
  const [newEspecialidad, setNewEspecialidad] = useState("");

  // Ecografías
  const [newEco, setNewEco] = useState("");

  // Estudios
  const [newEstudio, setNewEstudio] = useState({ name: "", price: "", note: "" });

  // Consultas
  const [newConsulta, setNewConsulta] = useState({ group: "", note: "", price: "" });
  const [newItemByGroup, setNewItemByGroup] = useState({});

  // Convenios
  const [newConvenio, setNewConvenio] = useState({ category: "", discount: "", partners: "" });

  // Equipo
  const [modeloEco, setModeloEco] = useState(settings.modeloEco);
  const [displayTimezone, setDisplayTimezone] = useState(settings.displayTimezone || "America/Caracas");

  const handleAddEspecialidad = () => {
    if (!newEspecialidad.trim()) return;
    dispatch(updateEspecialidadesAction([...settings.especialidades, newEspecialidad.trim()]));
    setNewEspecialidad("");
  };

  const handleDeleteEspecialidad = (index) => {
    dispatch(updateEspecialidadesAction(settings.especialidades.filter((_, i) => i !== index)));
  };

  const handleSaveContacto = () => {
    dispatch(updateContactoAction(contacto));
    toast.success("Contacto guardado");
  };

  const handleAddEco = () => {
    if (!newEco.trim()) return;
    dispatch(updateEcografiasAction([...settings.ecografias, newEco.trim()]));
    setNewEco("");
  };

  const handleDeleteEco = (index) => {
    dispatch(updateEcografiasAction(settings.ecografias.filter((_, i) => i !== index)));
  };

  const handleAddEstudio = () => {
    if (!newEstudio.name.trim()) return;
    dispatch(updateEstudiosAction([...settings.estudios, { ...newEstudio, id: Date.now() }]));
    setNewEstudio({ name: "", price: "", note: "" });
  };

  const handleDeleteEstudio = (id) => {
    dispatch(updateEstudiosAction(settings.estudios.filter((e) => e.id !== id)));
  };

  const handleAddConsulta = () => {
    if (!newConsulta.group.trim()) return;
    dispatch(updateConsultasAction([...settings.consultas, { ...newConsulta, id: Date.now(), items: [] }]));
    setNewConsulta({ group: "", note: "", price: "" });
  };

  const handleDeleteConsulta = (id) => {
    dispatch(updateConsultasAction(settings.consultas.filter((c) => c.id !== id)));
  };

  const handleAddConsultaItem = (groupId) => {
    const val = (newItemByGroup[groupId] || "").trim();
    if (!val) return;
    dispatch(updateConsultasAction(
      (settings.consultas || []).map((c) =>
        c.id === groupId ? { ...c, items: [...c.items, val] } : c
      )
    ));
    setNewItemByGroup({ ...newItemByGroup, [groupId]: "" });
  };

  const handleDeleteConsultaItem = (groupId, itemIndex) => {
    dispatch(updateConsultasAction(
      (settings.consultas || []).map((c) =>
        c.id === groupId ? { ...c, items: c.items.filter((_, i) => i !== itemIndex) } : c
      )
    ));
  };

  const handleAddConvenio = () => {
    if (!newConvenio.category.trim()) return;
    dispatch(updateConveniosAction([...settings.convenios, { ...newConvenio, id: Date.now() }]));
    setNewConvenio({ category: "", discount: "", partners: "" });
  };

  const handleDeleteConvenio = (id) => {
    dispatch(updateConveniosAction(settings.convenios.filter((c) => c.id !== id)));
  };

  const handleSaveModeloEco = () => {
    dispatch(updateModeloEcoAction(modeloEco));
    toast.success("Guardado");
  };

  const handleSaveDisplayTimezone = () => {
    dispatch(updateDisplayTimezoneAction(displayTimezone));
    toast.success("Guardado");
  };

  return (
    <Paper sx={{ p: 3, width: "100%" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Configuración
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Contacto" />
        <Tab label="Especialidades" />
        <Tab label="Ecografías" />
        <Tab label="Consultas" />
        <Tab label="Estudios e Imágenes" />
        <Tab label="Convenios" />
        <Tab label="Equipo" />
      </Tabs>

      {/* ── CONTACTO ── */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Teléfono" value={contacto.phone}
              onChange={(e) => setContacto({ ...contacto, phone: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="WhatsApp (con código de país, ej: 584147131791)"
              value={contacto.whatsapp}
              onChange={(e) => setContacto({ ...contacto, whatsapp: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Correo" value={contacto.mail}
              onChange={(e) => setContacto({ ...contacto, mail: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={2} label="Dirección"
              value={contacto.address}
              onChange={(e) => setContacto({ ...contacto, address: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Nombre de contacto" value={contacto.contactName}
              onChange={(e) => setContacto({ ...contacto, contactName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Cargo" value={contacto.contactTitle}
              onChange={(e) => setContacto({ ...contacto, contactTitle: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveContacto}>
              Guardar
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── ESPECIALIDADES ── */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          <TextField
            size="small" label="Nueva especialidad" value={newEspecialidad}
            onChange={(e) => setNewEspecialidad(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddEspecialidad()}
          />
          <IconButton color="primary" onClick={handleAddEspecialidad}>
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {(settings.especialidades || []).map((esp, i) => (
            <Chip key={i} label={esp} onDelete={() => handleDeleteEspecialidad(i)} />
          ))}
        </Box>
      </TabPanel>

      {/* ── ECOGRAFÍAS ── */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          <TextField
            size="small" label="Nueva ecografía" value={newEco}
            onChange={(e) => setNewEco(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddEco()}
          />
          <IconButton color="primary" onClick={handleAddEco}>
            <AddIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {(settings.ecografias || []).map((eco, i) => (
            <Chip key={i} label={eco} onDelete={() => handleDeleteEco(i)} />
          ))}
        </Box>
      </TabPanel>

      {/* ── CONSULTAS ── */}
      <TabPanel value={tab} index={3}>
        {(settings.consultas || []).map((c) => (
          <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{c.group}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.note}{c.price ? ` · ${c.price}` : ""}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => handleDeleteConsulta(c.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {c.items.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
                {c.items.map((item, i) => (
                  <Chip
                    key={i} size="small" label={item}
                    onDelete={() => handleDeleteConsultaItem(c.id, i)}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              <TextField
                size="small" placeholder="Añadir especialidad..."
                value={newItemByGroup[c.id] || ""}
                onChange={(e) => setNewItemByGroup({ ...newItemByGroup, [c.id]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleAddConsultaItem(c.id)}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => handleAddConsultaItem(c.id)}>
                <AddIcon />
              </IconButton>
            </Box>
          </Paper>
        ))}

        <Divider sx={{ my: 3 }} />
        <SectionLabel>Añadir grupo de consultas</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small" label="Nombre del grupo"
              value={newConsulta.group}
              onChange={(e) => setNewConsulta({ ...newConsulta, group: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth size="small" label="Precio (ej: $20)"
              value={newConsulta.price}
              onChange={(e) => setNewConsulta({ ...newConsulta, price: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth size="small" label="Nota"
              value={newConsulta.note}
              onChange={(e) => setNewConsulta({ ...newConsulta, note: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddConsulta}>
              Añadir grupo
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── ESTUDIOS E IMÁGENES ── */}
      <TabPanel value={tab} index={4}>
        {(settings.estudios || []).map((e) => (
          <Box
            key={e.id}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>{e.name}</Typography>
              {e.note && <Typography variant="caption" color="text.secondary">{e.note}</Typography>}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontWeight={700} color="primary.main">{e.price}</Typography>
              <IconButton size="small" onClick={() => handleDeleteEstudio(e.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />
        <SectionLabel>Añadir estudio</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small" label="Nombre"
              value={newEstudio.name}
              onChange={(e) => setNewEstudio({ ...newEstudio, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth size="small" label="Precio"
              value={newEstudio.price}
              onChange={(e) => setNewEstudio({ ...newEstudio, price: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth size="small" label="Nota"
              value={newEstudio.note}
              onChange={(e) => setNewEstudio({ ...newEstudio, note: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddEstudio}>
              Añadir estudio
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── CONVENIOS ── */}
      <TabPanel value={tab} index={5}>
        {(settings.convenios || []).map((c) => (
          <Box
            key={c.id}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderLeft: "3px solid", borderColor: "primary.main", pl: 2, py: 1, mb: 2 }}
          >
            <Box>
              <Typography variant="body2" fontWeight={700}>{c.category} — {c.discount}</Typography>
              <Typography variant="caption" color="text.secondary">{c.partners}</Typography>
            </Box>
            <IconButton size="small" onClick={() => handleDeleteConvenio(c.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />
        <SectionLabel>Añadir convenio</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Categoría"
              value={newConvenio.category}
              onChange={(e) => setNewConvenio({ ...newConvenio, category: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth size="small" label="Descuento"
              value={newConvenio.discount}
              onChange={(e) => setNewConvenio({ ...newConvenio, discount: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth size="small" label="Aliados"
              value={newConvenio.partners}
              onChange={(e) => setNewConvenio({ ...newConvenio, partners: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddConvenio}>
              Añadir convenio
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── EQUIPO ── */}
      <TabPanel value={tab} index={6}>
        <SectionLabel>Modelo de ecógrafo</SectionLabel>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Modelo" value={modeloEco}
            onChange={(e) => setModeloEco(e.target.value)}
            sx={{ width: 320 }}
          />
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveModeloEco}>
            Guardar
          </Button>
        </Box>

        <SectionLabel sx={{ mt: 3 }}>Zona horaria de visualización</SectionLabel>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Timezone (IANA)" value={displayTimezone}
            onChange={(e) => setDisplayTimezone(e.target.value)}
            sx={{ width: 320 }}
            helperText="Ejemplo: America/Caracas, America/Bogota, America/Lima"
          />
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveDisplayTimezone}>
            Guardar
          </Button>
        </Box>
      </TabPanel>
    </Paper>
  );
};

export default Settings;
